import React, {useEffect} from 'react'
import {useIntl} from 'react-intl'
import styles from './styles.module.scss'
import {useSelector} from 'react-redux'
import moment from 'moment'
import {useDispatch} from 'react-redux'
import {AppDispatch, RootState} from '../../../../../../state/store'
import {minutesToHoursAndMinutes} from '../../../../../../helpers/time-dates'
import {
  _getActualCellDurations,
  _getAllocatedCellOutDuration,
} from '../../../../../../state/V2/participant/participant.actions'
import {Participant} from '../../../../../../state/V2/openapi'
// import AlertTriggersDevOnly from './AlertTriggersDevOnly'

const InmateDetails = ({
  selectedInmateId,
  setSelectedInmateId,
  bothOpen,
}: {
  selectedInmateId: number
  setSelectedInmateId: (id: number | undefined) => void
  bothOpen?: boolean
}) => {
  const intl = useIntl()
  const dispatch = useDispatch<AppDispatch>()

  const cellTimeDurations = useSelector(
    (state: RootState) => state.participant.cellTimeDurations.data
  )
  const allocatedCellTime = useSelector(
    (state: RootState) => state.participant.allocatedCellTime.data
  )
  const selectedParticipant = useSelector((state: RootState) => state.participant.selected.data)

  useEffect(() => {
    if (!selectedInmateId || !selectedParticipant?.cell?.id) return
    let params = {
      participantId: selectedInmateId,
      cellId: selectedParticipant?.cell?.id,
      startTime: moment().startOf('week').day(moment().startOf('week').day()).toISOString(),
      endTime: moment().toISOString(),
    }
    dispatch(_getActualCellDurations(params))
    dispatch(_getAllocatedCellOutDuration(params))
  }, [dispatch, selectedInmateId, selectedParticipant?.cell?.id])

  const getAge = (birthday: string) => {
    const bday = new Date(birthday)
    const today = new Date()
    let age = today.getFullYear() - bday.getFullYear()
    var month = today.getMonth() - bday.getMonth()
    if (month < 0 || (month === 0 && today.getDate() < bday.getDate())) {
      age = age - 1
    }
    return age
  }

  function getTimeSince(timestamp: number | string | Date): string {
    if (timestamp === 'N/A') {
      return timestamp
    }

    let timestampDate: Date

    if (timestamp instanceof Date) {
      timestampDate = timestamp
    } else {
      timestampDate = new Date(timestamp)
    }

    if (isNaN(timestampDate.getTime())) {
      return 'Invalid timestamp'
    }

    const diff = Math.abs(Date.now() - timestampDate.getTime())
    const minutesSince = Math.floor(diff / 1000 / 60)

    if (minutesSince >= 60 && minutesSince < 120) {
      const hoursSince = Math.floor(minutesSince / 60)
      return `${hoursSince} Hour`
    } else if (minutesSince >= 120) {
      const hoursSince = Math.floor(minutesSince / 60)
      return `${hoursSince} Hours`
    } else if (minutesSince <= 1) {
      return `${minutesSince} Min`
    } else {
      return `${minutesSince} Min(s)`
    }
  }

  const getInmateDetails = (inmateData: Participant) => {
    const heartrate = inmateData.participantHeartrate?.heartrate?.toString()
    let gender = '?'
    switch (inmateData.gender.toLowerCase()) {
      case 'male':
        gender = intl.formatMessage({
          id: 'GENDER.MALE',
        })
        break
      case 'female':
        gender = intl.formatMessage({
          id: 'GENDER.FEMALE',
        })
        break
    }

    return {
      batteryPercent: inmateData.participantBattery?.batteryLevel?.toString(),
      heartrate,
      gender,
      inCellTime: minutesToHoursAndMinutes(cellTimeDurations?.inCellTime, true).toString(),
      allocatedOutTime: minutesToHoursAndMinutes(allocatedCellTime?.duration, true).toString(),
      outOfCellTime: minutesToHoursAndMinutes(
        Math.abs(cellTimeDurations?.outOfCellTime || 0),
        true
      ).toString(),
    }
  }

  if (!selectedParticipant) return <div>no selected inmate</div>

  const details = getInmateDetails(selectedParticipant)

  return (
    <div className={`${styles.inmatedetails} ${bothOpen && styles.bothopen}`}>
      <div className={styles.header}>
        <div>
          {intl.formatMessage({
            id: 'LIVETRACK.SIDEBAR.INMATE_DETAILS',
          })}
        </div>
        <CloseIcon
          onClick={() => {
            setSelectedInmateId(undefined)
          }}
          style={{cursor: 'pointer'}}
        />
      </div>
      <div className={styles.content}>
        <div className={styles.name}>
          {`${selectedParticipant?.firstName} ${selectedParticipant?.lastName}`}
        </div>
        <div className={styles.list}>
          <div className={styles.item}>
            <LocationIcon />
            {selectedParticipant.participantLocationTime?.location
              ? `${selectedParticipant?.participantLocationTime?.location}`
              : 'N/A'}
          </div>
          <div className={styles.item}>
            <GenderIcon />
            {details.gender}
          </div>
          <div className={styles.item}>
            <AgeIcon />
            {`${intl.formatMessage({
              id: 'AGE',
            })}: ${getAge(selectedParticipant?.birthDate)}`}
          </div>
        </div>
        <div className={styles.data}>
          <InmateDataBlock
            label={intl.formatMessage({
              id: 'LIVETRACK.SIDEBAR.LAST_PING',
            })}
            value={getTimeSince(
              selectedParticipant.participantHeartrate?.createdAt
                ? selectedParticipant.participantHeartrate?.createdAt
                : 0
            )}
            suffix=''
          />
          <InmateDataBlock
            label={intl.formatMessage({
              id: 'LIVETRACK.SIDEBAR.HEART_RATE',
            })}
            value={details.heartrate}
            suffix=''
          />
          <InmateDataBlock
            label={intl.formatMessage({
              id: 'LIVETRACK.SIDEBAR.BATTERY_LEVEL',
            })}
            value={details.batteryPercent}
            suffix='%'
          />
          <InmateDataBlock
            label={intl.formatMessage({
              id: 'LIVETRACK.SIDEBAR.ALLOCATED_OUT',
            })}
            value={details.allocatedOutTime}
            suffix=''
          />
          <InmateDataBlock
            label={intl.formatMessage({
              id: 'LIVETRACK.SIDEBAR.IN_CELL_TIME',
            })}
            value={details.inCellTime}
            suffix={''}
          />
          <InmateDataBlock
            label={intl.formatMessage({
              id: 'LIVETRACK.SIDEBAR.OUT_OF_CELL_TIME',
            })}
            value={details.outOfCellTime}
            suffix=''
          />
        </div>
        {/* Only uncomment below for development */}
        {/* <AlertTriggersDevOnly /> */}
      </div>
    </div>
  )
}

const InmateDataBlock = ({
  label,
  value,
  suffix = '',
  fallback = 'N/A',
}: {
  label: string
  value?: string | null | number
  suffix?: string
  fallback?: string
}) => {
  return (
    <div className={styles.block}>
      <div className={styles.value}>
        {value !== undefined && value !== null && value !== '' ? value + suffix : fallback}
      </div>
      <div className={styles.label}>{label}</div>
    </div>
  )
}

const CloseIcon = (props: React.SVGProps<SVGSVGElement>) => {
  return (
    <svg
      {...props}
      width='13'
      height='13'
      viewBox='0 0 13 13'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
    >
      <path
        d='M12.3175 11.4332C12.3756 11.4912 12.4217 11.5602 12.4531 11.636C12.4845 11.7119 12.5007 11.7932 12.5007 11.8753C12.5007 11.9575 12.4845 12.0388 12.4531 12.1147C12.4217 12.1905 12.3756 12.2595 12.3175 12.3175C12.2595 12.3756 12.1905 12.4217 12.1147 12.4531C12.0388 12.4845 11.9575 12.5007 11.8753 12.5007C11.7932 12.5007 11.7119 12.4845 11.636 12.4531C11.5602 12.4217 11.4912 12.3756 11.4332 12.3175L6.25035 7.13394L1.06753 12.3175C0.95026 12.4348 0.7912 12.5007 0.625347 12.5007C0.459495 12.5007 0.300435 12.4348 0.18316 12.3175C0.0658846 12.2003 3.26935e-09 12.0412 0 11.8753C-3.26935e-09 11.7095 0.0658846 11.5504 0.18316 11.4332L5.36675 6.25035L0.18316 1.06753C0.0658846 0.95026 0 0.7912 0 0.625347C0 0.459495 0.0658846 0.300435 0.18316 0.18316C0.300435 0.0658846 0.459495 0 0.625347 0C0.7912 0 0.95026 0.0658846 1.06753 0.18316L6.25035 5.36675L11.4332 0.18316C11.5504 0.0658846 11.7095 -3.26935e-09 11.8753 0C12.0412 3.26935e-09 12.2003 0.0658846 12.3175 0.18316C12.4348 0.300435 12.5007 0.459495 12.5007 0.625347C12.5007 0.7912 12.4348 0.95026 12.3175 1.06753L7.13394 6.25035L12.3175 11.4332Z'
        fill='#A1A09A'
      />
    </svg>
  )
}

const LocationIcon = () => {
  return (
    <svg width='16' height='18' viewBox='0 0 16 18' fill='none' xmlns='http://www.w3.org/2000/svg'>
      <path
        d='M14.6914 4.64648C14.3237 3.79859 13.7935 3.0309 13.1309 2.38672C12.466 1.73672 11.6822 1.22082 10.8223 0.867188C9.92773 0.496094 8.97852 0.308594 8 0.308594C7.02148 0.308594 6.07227 0.496094 5.17773 0.865234C4.3125 1.22266 3.53711 1.73438 2.86914 2.38477C2.20688 3.02931 1.67679 3.79691 1.30859 4.64453C0.927734 5.52344 0.734375 6.45703 0.734375 7.41797C0.734375 8.79688 1.06445 10.1699 1.71289 11.4941C2.23438 12.5586 2.96289 13.5957 3.88086 14.582C5.44922 16.2656 7.09375 17.2949 7.56055 17.5703C7.69305 17.6486 7.84416 17.6897 7.99805 17.6895C8.15039 17.6895 8.30078 17.6504 8.43555 17.5703C8.90234 17.2949 10.5469 16.2656 12.1152 14.582C13.0332 13.5977 13.7617 12.5586 14.2832 11.4941C14.9355 10.1719 15.2656 8.80078 15.2656 7.41992C15.2656 6.45898 15.0723 5.52539 14.6914 4.64648ZM8 16.1914C6.71289 15.373 2.14062 12.1328 2.14062 7.41992C2.14062 5.89844 2.74805 4.46875 3.85156 3.39062C4.95898 2.31055 6.43164 1.71484 8 1.71484C9.56836 1.71484 11.041 2.31055 12.1484 3.39258C13.252 4.46875 13.8594 5.89844 13.8594 7.41992C13.8594 12.1328 9.28711 15.373 8 16.1914ZM8 4.13672C6.10156 4.13672 4.5625 5.67578 4.5625 7.57422C4.5625 9.47266 6.10156 11.0117 8 11.0117C9.89844 11.0117 11.4375 9.47266 11.4375 7.57422C11.4375 5.67578 9.89844 4.13672 8 4.13672ZM9.54688 9.12109C9.34399 9.32456 9.10287 9.48591 8.8374 9.59585C8.57193 9.7058 8.28734 9.76217 8 9.76172C7.41602 9.76172 6.86719 9.5332 6.45312 9.12109C6.24965 8.91821 6.08831 8.67709 5.97836 8.41162C5.86842 8.14615 5.81205 7.86156 5.8125 7.57422C5.8125 6.99023 6.04102 6.44141 6.45312 6.02734C6.86719 5.61328 7.41602 5.38672 8 5.38672C8.58398 5.38672 9.13281 5.61328 9.54688 6.02734C9.96094 6.44141 10.1875 6.99023 10.1875 7.57422C10.1875 8.1582 9.96094 8.70703 9.54688 9.12109Z'
        fill='#7D7D7D'
      />
    </svg>
  )
}
const GenderIcon = () => {
  return (
    <svg width='14' height='14' viewBox='0 0 14 14' fill='none' xmlns='http://www.w3.org/2000/svg'>
      <path
        d='M13.4329 12.2497C12.481 10.6041 11.0142 9.42412 9.30229 8.86475C10.1491 8.36066 10.8069 7.59255 11.1749 6.67837C11.5429 5.76419 11.6006 4.7545 11.3392 3.80435C11.0778 2.85419 10.5117 2.01612 9.7279 1.41883C8.94408 0.821532 7.98587 0.498047 7.00041 0.498047C6.01496 0.498047 5.05674 0.821532 4.27293 1.41883C3.48911 2.01612 2.92304 2.85419 2.66163 3.80435C2.40022 4.7545 2.45793 5.76419 2.82591 6.67837C3.19388 7.59255 3.85177 8.36066 4.69854 8.86475C2.98666 9.4235 1.51979 10.6035 0.567913 12.2497C0.533006 12.3067 0.509853 12.37 0.499819 12.436C0.489785 12.502 0.493074 12.5694 0.509491 12.6341C0.525908 12.6988 0.555122 12.7596 0.595408 12.8128C0.635694 12.8661 0.686237 12.9107 0.744054 12.9441C0.801871 12.9775 0.865791 12.999 0.932043 13.0073C0.998295 13.0156 1.06554 13.0105 1.1298 12.9924C1.19407 12.9743 1.25405 12.9435 1.30622 12.9018C1.35838 12.8601 1.40168 12.8084 1.43354 12.7497C2.61104 10.7147 4.69229 9.49975 7.00041 9.49975C9.30854 9.49975 11.3898 10.7147 12.5673 12.7497C12.5992 12.8084 12.6424 12.8601 12.6946 12.9018C12.7468 12.9435 12.8068 12.9743 12.871 12.9924C12.9353 13.0105 13.0025 13.0156 13.0688 13.0073C13.135 12.999 13.199 12.9775 13.2568 12.9441C13.3146 12.9107 13.3651 12.8661 13.4054 12.8128C13.4457 12.7596 13.4749 12.6988 13.4913 12.6341C13.5078 12.5694 13.511 12.502 13.501 12.436C13.491 12.37 13.4678 12.3067 13.4329 12.2497ZM3.50041 4.99975C3.50041 4.30751 3.70568 3.63082 4.09027 3.05525C4.47485 2.47968 5.02148 2.03108 5.66102 1.76617C6.30056 1.50126 7.0043 1.43195 7.68323 1.567C8.36216 1.70205 8.9858 2.03539 9.47529 2.52487C9.96477 3.01436 10.2981 3.638 10.4332 4.31693C10.5682 4.99586 10.4989 5.6996 10.234 6.33914C9.96908 6.97868 9.52048 7.52531 8.94491 7.90989C8.36934 8.29448 7.69265 8.49975 7.00041 8.49975C6.07246 8.49875 5.1828 8.12969 4.52664 7.47353C3.87047 6.81736 3.50141 5.9277 3.50041 4.99975Z'
        fill='#7F7E77'
      />
    </svg>
  )
}

const AgeIcon = () => {
  return (
    <svg width='14' height='16' viewBox='0 0 14 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
      <path
        d='M9.00004 2.5C9.00004 2.10444 8.88274 1.71776 8.66298 1.38886C8.44322 1.05996 8.13086 0.803617 7.76541 0.652242C7.39995 0.500867 6.99782 0.46126 6.60986 0.53843C6.2219 0.615601 5.86553 0.806082 5.58582 1.08579C5.30612 1.36549 5.11564 1.72186 5.03847 2.10982C4.9613 2.49778 5.0009 2.89992 5.15228 3.26537C5.30365 3.63082 5.56 3.94318 5.8889 4.16294C6.2178 4.3827 6.60448 4.5 7.00004 4.5C7.53047 4.5 8.03918 4.28929 8.41425 3.91421C8.78932 3.53914 9.00004 3.03043 9.00004 2.5ZM7.00004 3.5C6.80226 3.5 6.60892 3.44135 6.44447 3.33147C6.28002 3.22159 6.15185 3.06541 6.07616 2.88268C6.00047 2.69996 5.98067 2.49889 6.01925 2.30491C6.05784 2.11093 6.15308 1.93275 6.29293 1.79289C6.43278 1.65304 6.61097 1.5578 6.80495 1.51922C6.99893 1.48063 7.2 1.50043 7.38272 1.57612C7.56545 1.65181 7.72163 1.77998 7.83151 1.94443C7.94139 2.10888 8.00004 2.30222 8.00004 2.5C8.00004 2.76522 7.89468 3.01957 7.70715 3.20711C7.51961 3.39464 7.26525 3.5 7.00004 3.5ZM13.4688 5.48188C13.4119 5.20185 13.2589 4.9505 13.0364 4.77122C12.8139 4.59195 12.5358 4.49602 12.25 4.5H1.75004C1.46509 4.49999 1.18869 4.59734 0.966639 4.77592C0.744588 4.95449 0.590213 5.20357 0.529094 5.48189C0.467975 5.7602 0.503781 6.05105 0.630579 6.30623C0.757377 6.56141 0.967555 6.76562 1.22629 6.885H1.23441L4.40629 8.28188L3.09379 13.2644C2.95646 13.5669 2.94492 13.9116 3.06172 14.2226C3.17852 14.5336 3.41409 14.7855 3.7166 14.9228C4.01911 15.0601 4.36379 15.0717 4.67481 14.9549C4.98583 14.8381 5.23771 14.6025 5.37504 14.3L7.00004 11.4956L8.62504 14.3C8.76945 14.5896 9.02055 14.8119 9.32553 14.9202C9.63051 15.0285 9.96559 15.0142 10.2603 14.8805C10.555 14.7467 10.7863 14.5039 10.9056 14.2031C11.025 13.9023 11.0229 13.5669 10.9 13.2675L9.58754 8.285L12.7594 6.88813H12.7675C13.0304 6.77273 13.2446 6.56884 13.3727 6.31192C13.5008 6.05499 13.5348 5.76128 13.4688 5.48188ZM12.3563 5.97563L8.79816 7.54188C8.68924 7.58987 8.601 7.67516 8.54934 7.78239C8.49767 7.88962 8.48595 8.01177 8.51629 8.12688L9.94566 13.5625C9.95295 13.5916 9.96321 13.6199 9.97629 13.6469C9.99726 13.6917 10.0044 13.7418 9.99692 13.7907C9.9894 13.8397 9.96752 13.8853 9.93404 13.9218C9.90057 13.9583 9.85701 13.984 9.8089 13.9957C9.76079 14.0074 9.71028 14.0045 9.66379 13.9875C9.60163 13.9647 9.55106 13.9182 9.52316 13.8581C9.51683 13.8446 9.50974 13.8315 9.50191 13.8188L7.43254 10.25C7.38859 10.1742 7.3255 10.1113 7.2496 10.0676C7.17369 10.0239 7.08763 10.0009 7.00004 10.0009C6.91244 10.0009 6.82639 10.0239 6.75048 10.0676C6.67458 10.1113 6.61149 10.1742 6.56754 10.25L4.50004 13.8156C4.49222 13.8283 4.48512 13.8415 4.47879 13.855C4.45089 13.915 4.40032 13.9616 4.33816 13.9844C4.29167 14.0014 4.24117 14.0043 4.19305 13.9926C4.14494 13.9809 4.10138 13.9551 4.06791 13.9187C4.03444 13.8822 4.01255 13.8366 4.00503 13.7876C3.99751 13.7387 4.00469 13.6886 4.02566 13.6438C4.03874 13.6168 4.049 13.5885 4.05629 13.5594L5.48379 8.125C5.51413 8.0099 5.50241 7.88775 5.45074 7.78051C5.39907 7.67328 5.31084 7.588 5.20191 7.54L1.64379 5.97563C1.59271 5.95125 1.55142 5.91028 1.52666 5.85939C1.50189 5.8085 1.49513 5.75072 1.50747 5.69549C1.5198 5.64026 1.55052 5.59085 1.59458 5.55534C1.63864 5.51983 1.69345 5.50032 1.75004 5.5H12.25C12.3068 5.4999 12.3619 5.51911 12.4063 5.55447C12.4507 5.58983 12.4817 5.63924 12.4943 5.69458C12.5069 5.74992 12.5002 5.80789 12.4755 5.85897C12.4508 5.91004 12.4094 5.95118 12.3582 5.97563H12.3563Z'
        fill='#7F7E77'
      />
    </svg>
  )
}



export default InmateDetails
